import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGetAssignmentMarksQuery } from "../../features/assignmentMark/assignmentMarkAPI";
import { useGetQuizMarksQuery } from "../../features/quizMark/quizMarkAPI";

function LeaderBoard() {
  const {
    data: assignmentMarks,
    isLoading: assignmentMarksLoading,
    isError: assignmentMarksError,
  } = useGetAssignmentMarksQuery();
  const {
    data: quizMarks,
    isLoading: quizMarksLoading,
    isError: quizMarksError,
  } = useGetQuizMarksQuery();

  const [leaderBoard, setLeaderBoard] = useState([]);
  const [studentPosition, setStudentPosition] = useState(0);
  const userID = useSelector((state) => state.auth.user.id);

  //memoized

  const memoizedLeaderboard = useMemo(() => {
    let leaderBoardObject = {};

    let studentWiseAssignmentMarks = {};

    assignmentMarks?.forEach((assignment) => {
      if (studentWiseAssignmentMarks[assignment?.student_id]) {
        const prevInstance = {
          ...studentWiseAssignmentMarks[assignment?.student_id],
        };
        studentWiseAssignmentMarks[assignment?.student_id] = {
          student_id: assignment?.student_id,
          assignmentMarks: prevInstance.assignmentMarks + assignment?.mark,
          quizMarks: 0,
          total: prevInstance.total + assignment?.mark,
          name: assignment.student_name,
          rank: 0,
        };
      } else {
        studentWiseAssignmentMarks[assignment?.student_id] = {
          student_id: assignment?.student_id,
          assignmentMarks: assignment?.mark,
          quizMarks: 0,
          total: assignment?.mark,
          name: assignment.student_name,
          rank: 0,
        };
      }
    });

    let studentWiseQuizMarks = {};
    quizMarks?.forEach((quiz) => {
      if (studentWiseQuizMarks[quiz?.student_id]) {
        const prevInstance = studentWiseQuizMarks[quiz?.student_id];
        studentWiseQuizMarks[quiz?.student_id] = {
          ...prevInstance,
          quizMarks: prevInstance.quizMarks + quiz?.mark,
          total: prevInstance.total + quiz?.mark,
        };
      } else {
        studentWiseQuizMarks[quiz?.student_id] = {
          student_id: quiz?.student_id,
          assignmentMarks: 0,
          quizMarks: quiz?.mark,
          total: quiz?.mark,
          name: quiz?.student_name,
          rank: 0,
        };
      }
    });

    leaderBoardObject = { ...studentWiseAssignmentMarks };
    const studentWiseQuizMarksArray = Object.values(studentWiseQuizMarks);

    studentWiseQuizMarksArray?.forEach((quiz) => {
      if (leaderBoardObject[quiz.student_id]) {
        const prevTotal = leaderBoardObject[quiz.student_id].total;
        leaderBoardObject[quiz.student_id] = {
          ...leaderBoardObject[quiz.student_id],
          quizMarks: quiz.quizMarks,
          total: prevTotal + quiz.total,
        };
      } else {
        leaderBoardObject[quiz.student_id] = {
          student_id: quiz.student_id,
          assignmentMarks: 0,
          quizMarks: quiz.total,
          total: quiz.total,
          name: quiz.name,
          rank: 0,
        };
      }
    });

    let sortedLeaderBoard = Object.values(leaderBoardObject).sort(
      (a, b) => b.total - a.total
    );

    const totalMarksArray = Object.values(leaderBoardObject).map(
      (student) => student.total
    );

    const totalMarksSet = new Set(totalMarksArray);
    const sortedTotalMarks = [...totalMarksSet].sort((a, b) => b - a);
    let marksToPositionMap = {};
    sortedTotalMarks.forEach((mark, index) => {
      marksToPositionMap[mark] = index + 1;
    });

    sortedLeaderBoard?.forEach((student) => {
      student.rank = marksToPositionMap[student.total];
    });

    return {
      top20Position: sortedLeaderBoard?.filter((student) => student.rank < 21),
      studentPosition: sortedLeaderBoard?.find(
        (student) => student.student_id === userID
      ),
    };
  }, [assignmentMarks, quizMarks, userID]);

  useEffect(() => {
    setLeaderBoard(memoizedLeaderboard.top20Position);
    setStudentPosition(memoizedLeaderboard.studentPosition);
  }, [memoizedLeaderboard]);

  let studentContent = null;
  let leaderboardContent = null;

  if (assignmentMarksLoading || quizMarksLoading) {
    studentContent = (
      <td className="table-td text-center font-bold" colSpan="5">
        Loading...
      </td>
    );
    leaderboardContent = (
      <td className="table-td text-center font-bold" colSpan="5">
        Loading...
      </td>
    );
  } else if (assignmentMarksError || quizMarksError) {
    studentContent = (
      <td className="table-td text-center font-bold" colSpan="5">
        Something went wrong!
      </td>
    );
    leaderboardContent = (
      <td className="table-td text-center font-bold" colSpan="5">
        Something Went Wrong!
      </td>
    );
  } else {
    studentContent = studentPosition?.total ? (
      <>
        <td className="table-td text-center font-bold">
          {studentPosition?.rank}
        </td>
        <td className="table-td text-center font-bold">
          {studentPosition?.name}
        </td>
        <td className="table-td text-center font-bold">
          {studentPosition?.quizMarks}
        </td>
        <td className="table-td text-center font-bold">
          {studentPosition?.assignmentMarks}
        </td>
        <td className="table-td text-center font-bold">
          {studentPosition?.total}
        </td>
      </>
    ) : (
      <td className="table-td text-center font-bold" colSpan="5">
        You didn't submit any Quiz or Assignment Yet!
      </td>
    );

    leaderboardContent =
      leaderBoard?.length > 0 ? (
        leaderBoard?.map((student) => (
          <tr
            className="border-b border-slate-600/50"
            key={student?.student_id}
          >
            <td className="table-td text-center">{student?.rank}</td>
            <td className="table-td text-center">{student?.name}</td>
            <td className="table-td text-center">{student?.quizMarks}</td>
            <td className="table-td text-center">{student?.assignmentMarks}</td>
            <td className="table-td text-center">{student?.total}</td>
          </tr>
        ))
      ) : (
        <tr className="border-b border-slate-600/50">
          <td className="table-td text-center font-bold" colSpan={5}>
            Nobody Submitted anything yet!
          </td>
        </tr>
      );
  }

  return (
    <section className="py-6 bg-primary">
      <div className="mx-auto max-w-7xl px-5 lg:px-0">
        <div>
          <h3 className="text-lg font-bold">Your Position in Leaderboard</h3>
          <table className="text-base w-full border border-slate-600/50 rounded-md my-4">
            <thead>
              <tr>
                <th className="table-th !text-center">Rank</th>
                <th className="table-th !text-center">Name</th>
                <th className="table-th !text-center">Quiz Mark</th>
                <th className="table-th !text-center">Assignment Mark</th>
                <th className="table-th !text-center">Total</th>
              </tr>
            </thead>

            <tbody>
              <tr className="border-2 border-cyan">{studentContent}</tr>
            </tbody>
          </table>
        </div>

        <div className="my-8">
          <h3 className="text-lg font-bold">Top 20 Result</h3>
          <table className="text-base w-full border border-slate-600/50 rounded-md my-4">
            <thead>
              <tr className="border-b border-slate-600/50">
                <th className="table-th !text-center">Rank</th>
                <th className="table-th !text-center">Name</th>
                <th className="table-th !text-center">Quiz Mark</th>
                <th className="table-th !text-center">Assignment Mark</th>
                <th className="table-th !text-center">Total</th>
              </tr>
            </thead>

            <tbody>{leaderboardContent}</tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default LeaderBoard;
